import React, { createContext, useContext, useState, useEffect } from 'react';
import { toast } from 'sonner';

export interface HostelComparisonData {
    id: string;
    name: string;
    image: string;
    price: number;
    rating: number;
    location: string;
    amenities: string[];
    roomTypes: string[];
    distance: string;
}

interface ComparisonContextType {
    selectedHostels: HostelComparisonData[];
    addToCompare: (hostel: HostelComparisonData) => void;
    removeFromCompare: (hostelId: string) => void;
    clearComparison: () => void;
    isInComparison: (hostelId: string) => boolean;
}

const ComparisonContext = createContext<ComparisonContextType | undefined>(undefined);

export const ComparisonProvider = ({ children }: { children: React.ReactNode }) => {
    const [selectedHostels, setSelectedHostels] = useState<HostelComparisonData[]>([]);

    useEffect(() => {
        const saved = localStorage.getItem('hostel_comparison');
        if (saved) {
            try {
                setSelectedHostels(JSON.parse(saved));
            } catch (e) {
                console.error('Failed to parse saved comparison data', e);
            }
        }
    }, []);

    useEffect(() => {
        localStorage.setItem('hostel_comparison', JSON.stringify(selectedHostels));
    }, [selectedHostels]);

    const addToCompare = (hostel: HostelComparisonData) => {
        if (selectedHostels.length >= 3) {
            toast.error("You can only compare up to 3 hostels at a time.");
            return;
        }
        if (selectedHostels.find(h => h.id === hostel.id)) {
            toast.info("This hostel is already in your comparison list.");
            return;
        }
        setSelectedHostels([...selectedHostels, hostel]);
        toast.success(`${hostel.name} added to comparison.`);
    };

    const removeFromCompare = (hostelId: string) => {
        setSelectedHostels(selectedHostels.filter(h => h.id !== hostelId));
    };

    const clearComparison = () => {
        setSelectedHostels([]);
    };

    const isInComparison = (hostelId: string) => {
        return selectedHostels.some(h => h.id === hostelId);
    };

    return (
        <ComparisonContext.Provider value={{ selectedHostels, addToCompare, removeFromCompare, clearComparison, isInComparison }}>
            {children}
        </ComparisonContext.Provider>
    );
};

/* eslint-disable-next-line react-refresh/only-export-components -- context and hook exported together */
export const useComparison = () => {
    const context = useContext(ComparisonContext);
    if (context === undefined) {
        throw new Error('useComparison must be used within a ComparisonProvider');
    }
    return context;
};
